import { useState, useRef, useEffect } from 'react';

const B_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <circle cx="18" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.4"/>
    <circle cx="6" cy="12" r="2.5" stroke="currentColor" strokeWidth="1.4"/>
    <circle cx="18" cy="19" r="2.5" stroke="currentColor" strokeWidth="1.4"/>
    <path d="M8.5 10.5l7-4M8.5 13.5l7 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
  </svg>
);

export default function ContentActionMenu({
  context = 'thread',
  contentId,
  contentType = 'session',
  messages = [],
  userId,
  supabase,
  sessionId,
}) {
  const [open, setOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null); // 'pull' | 'route' | 'share'
  const [copyDone, setCopyDone] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (!menuRef.current?.contains(e.target)) {
        setOpen(false);
        setActiveMenu(null);
        setSearchQuery('');
        setSearchResults([]);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  function formatMessagesAsText() {
    return messages
      .filter(m => m.role === 'user' || m.role === 'assistant')
      .map(m => `${m.role === 'user' ? 'You' : 'Shift'}: ${m.content}`)
      .join('\n\n');
  }

  async function handleCopy() {
    const text = formatMessagesAsText();
    await navigator.clipboard.writeText(text);
    setCopyDone(true);
    setTimeout(() => { setCopyDone(false); setOpen(false); setActiveMenu(null); }, 1500);
  }

  async function handleSearch(query) {
    setSearchQuery(query);
    if (!query.trim() || !userId || !supabase) return;
    setSearching(true);
    try {
      const [artRes, hlRes] = await Promise.all([
        supabase.from('artifacts').select('id, title, artifact_type, created_at').eq('user_id', userId).eq('is_archived', false).ilike('title', `%${query}%`).limit(5),
        supabase.from('highlights').select('id, text, created_at').eq('user_id', userId).ilike('text', `%${query}%`).limit(5),
      ]);
      const results = [
        ...(artRes.data || []).map(a => ({ id: a.id, label: a.title, type: 'artifact', date: a.created_at })),
        ...(hlRes.data || []).map(h => ({ id: h.id, label: h.text.slice(0, 60), type: 'highlight', date: h.created_at })),
      ];
      setSearchResults(results);
    } catch (e) { console.error('search:', e); }
    setSearching(false);
  }

  const ROUTE_DESTINATIONS = [
    { label: 'Save to Notes', desc: 'capture as a note', color: '#7a9e82', feature: 'notes' },
    { label: 'Add to Ideas', desc: 'save as an idea', color: '#9b8fc4', feature: 'ideas' },
    { label: 'Add to Playbook', desc: 'save as a framework', color: '#c8a96e', feature: 'playbook' },
  ];

  const menuStyle = {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    right: 0,
    background: '#1e1e2a',
    border: '1px solid #2a2a2a',
    borderRadius: 10,
    padding: 8,
    width: 220,
    zIndex: 9999,
    fontFamily: 'Georgia, serif',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  };

  const itemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '7px 8px',
    borderRadius: 6,
    cursor: 'pointer',
    background: 'none',
  };

  return (
    <div ref={menuRef} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <div
        onClick={() => { setOpen(p => !p); setActiveMenu(open ? null : 'share'); }}
        style={{ cursor: 'pointer', color: open ? '#c8a96e' : '#555', display: 'flex', alignItems: 'center' }}
        onMouseEnter={e => { if (!open) e.currentTarget.style.color = '#7a9e82'; }}
        onMouseLeave={e => { if (!open) e.currentTarget.style.color = '#555'; }}
      >
        {B_ICON}
      </div>

      {open && (
        <div style={menuStyle}>
          {/* Tab bar */}
          <div style={{ display: 'flex', borderBottom: '1px solid #2a2a2a', marginBottom: 4 }}>
            {['pull', 'route', 'share'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveMenu(tab)}
                style={{
                  flex: 1,
                  padding: '6px 4px',
                  fontSize: 11,
                  fontFamily: 'Georgia, serif',
                  color: activeMenu === tab ? '#c8a96e' : '#444',
                  borderBottom: `2px solid ${activeMenu === tab ? '#c8a96e' : 'transparent'}`,
                  cursor: 'pointer',
                  background: 'none',
                  border: 'none',
                  textTransform: 'capitalize',
                }}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* SHARE */}
          {activeMenu === 'share' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ color: '#2a2a2a', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', padding: '4px 8px' }}>Send outside Shift</div>
              {[
                { label: copyDone ? 'Copied ✓' : 'Copy to clipboard', desc: 'plain text', onClick: handleCopy },
                { label: 'Send via email', desc: 'coming soon', onClick: () => {} },
                { label: 'Download as PDF', desc: 'coming soon', onClick: () => {} },
              ].map((item, i) => (
                <div
                  key={i}
                  onClick={item.onClick}
                  style={itemStyle}
                  onMouseEnter={e => e.currentTarget.style.background = '#222'}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, color: copyDone && i === 0 ? '#7a9e82' : '#888' }}>{item.label}</div>
                    <div style={{ fontSize: 10, color: '#333', fontStyle: 'italic' }}>{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ROUTE */}
          {activeMenu === 'route' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ color: '#2a2a2a', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', padding: '4px 8px' }}>Shift suggests</div>
              {ROUTE_DESTINATIONS.map((dest, i) => (
                <div
                  key={i}
                  style={itemStyle}
                  onMouseEnter={e => e.currentTarget.style.background = '#222'}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}
                >
                  <div style={{ width: 26, height: 26, borderRadius: 5, background: dest.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: dest.color }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: dest.color }}>{dest.label}</div>
                    <div style={{ fontSize: 10, color: '#333', fontStyle: 'italic' }}>{dest.desc}</div>
                  </div>
                </div>
              ))}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px' }}>
                <div style={{ flex: 1, height: '0.5px', background: '#2a2a2a' }} />
                <span style={{ color: '#2a2a2a', fontSize: 10, fontStyle: 'italic' }}>or search</span>
                <div style={{ flex: 1, height: '0.5px', background: '#2a2a2a' }} />
              </div>
              <div style={{ position: 'relative', padding: '0 4px' }}>
                <input
                  placeholder="Search features..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  style={{ width: '100%', background: '#111', border: '1px solid #272736', borderRadius: 6, color: '#888', fontFamily: 'Georgia, serif', fontSize: 12, padding: '6px 10px', outline: 'none' }}
                />
              </div>
            </div>
          )}

          {/* PULL */}
          {activeMenu === 'pull' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ color: '#2a2a2a', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', padding: '4px 8px' }}>Shift suggests</div>
              <div style={{ color: '#333', fontSize: 11, fontStyle: 'italic', padding: '8px 12px', textAlign: 'center' }}>
                Send a message first — Shift will find relevant content.
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px' }}>
                <div style={{ flex: 1, height: '0.5px', background: '#2a2a2a' }} />
                <span style={{ color: '#2a2a2a', fontSize: 10, fontStyle: 'italic' }}>or search</span>
                <div style={{ flex: 1, height: '0.5px', background: '#2a2a2a' }} />
              </div>
              <div style={{ position: 'relative', padding: '0 4px' }}>
                <input
                  placeholder="Search anything..."
                  value={searchQuery}
                  onChange={e => handleSearch(e.target.value)}
                  style={{ width: '100%', background: '#111', border: '1px solid #272736', borderRadius: 6, color: '#888', fontFamily: 'Georgia, serif', fontSize: 12, padding: '6px 10px', outline: 'none' }}
                />
              </div>
              {searchResults.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 4 }}>
                  {searchResults.map((r, i) => (
                    <div
                      key={i}
                      style={itemStyle}
                      onMouseEnter={e => e.currentTarget.style.background = '#222'}
                      onMouseLeave={e => e.currentTarget.style.background = 'none'}
                    >
                      <span style={{ fontSize: 9, color: r.type === 'artifact' ? '#c8a96e' : '#9b8fc4', textTransform: 'uppercase', letterSpacing: '0.06em', width: 50, flexShrink: 0 }}>{r.type}</span>
                      <span style={{ fontSize: 11, color: '#666' }}>{r.label}</span>
                    </div>
                  ))}
                </div>
              )}
              {searching && <div style={{ color: '#333', fontSize: 11, fontStyle: 'italic', padding: '4px 12px' }}>searching···</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
